/** @format */

import { FC, useEffect, useRef, useState } from "react";
import type { Step } from "../../types/sop";
import Modal from "../../components/ui/Modal";
import ReferenceFileUploader from "./ReferenceFileUploader";
import StepSelectorList from "./StepSelectorList";
import { Button } from "../../components/ui/button";
import type { ReferenceFileKind } from "../../store/useReviewCommitStore";
import { feedbackService, type FeedbackItem } from "../../api/feedbackService";
import { cn } from "../../utils/cn";

interface AccordionSectionProps {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const AccordionSection: FC<AccordionSectionProps> = ({
  title,
  open,
  onToggle,
  children,
}) => (
  <div className="rounded-xl border border-slate-700/60 bg-slate-900/30">
    <button
      type="button"
      className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-slate-200 hover:bg-slate-800/40 transition-colors rounded-t-xl"
      onClick={onToggle}
    >
      <span>{title}</span>
      <span className="text-xs text-slate-400">{open ? "Hide" : "Show"}</span>
    </button>
    {open && <div className="px-4 pb-4">{children}</div>}
  </div>
);

interface StartReviewModalProps {
  open: boolean;
  onClose: () => void;
  sopId: string;
  versionId?: string;
  steps: Step[];

  referenceFiles: File[];
  onReferenceFilesChange: (files: File[]) => void;
  defaultReferenceFileKind?: ReferenceFileKind;
  onDefaultReferenceFileKindChange?: (kind: ReferenceFileKind) => void;
  referenceFileKinds?: Record<string, ReferenceFileKind>;
  onReferenceFileKindChange?: (
    fileKey: string,
    kind: ReferenceFileKind,
  ) => void;
  selectedStepIndices: number[];
  onSelectedStepIndicesChange: (indices: number[]) => void;
  selectedFeedbackIds: string[];
  onSelectedFeedbackIdsChange: (ids: string[]) => void;

  primaryLabel?: string;
  onPrimary: () => void | Promise<void>;
  isRegenerating?: boolean;
  regenerateError?: string | null;
}

const StartReviewModal: FC<StartReviewModalProps> = ({
  open,
  onClose,
  sopId,
  versionId,
  steps,
  referenceFiles,
  onReferenceFilesChange,
  defaultReferenceFileKind,
  onDefaultReferenceFileKindChange,
  referenceFileKinds,
  selectedStepIndices,
  onSelectedStepIndicesChange,
  selectedFeedbackIds,
  onSelectedFeedbackIdsChange,
  primaryLabel = "Regenerate with AI",
  onPrimary,
  isRegenerating = false,
  regenerateError,
}) => {
  const [openFeedbacks, setOpenFeedbacks] = useState<FeedbackItem[]>([]);
  const [sourcesOpen, setSourcesOpen] = useState(true);
  const [feedbackOpen, setFeedbackOpen] = useState(true);
  const [stepsOpen, setStepsOpen] = useState(true);
  const didAutoSelectFeedbacks = useRef(false);

  useEffect(() => {
    if (!open) return;
    const el = document.getElementById("regenerate-steps-modal-body");
    if (el) el.scrollTop = 0;
    setSourcesOpen(true);
    setFeedbackOpen(true);
    setStepsOpen(true);
  }, [open]);

  useEffect(() => {
    if (!open || !sopId || !versionId) {
      setOpenFeedbacks([]);
      didAutoSelectFeedbacks.current = false;
      return;
    }
    let mounted = true;
    void feedbackService
      .list(sopId, {
        versionId,
        status: "open",
      })
      .then((response) => {
        if (!mounted) return;
        // The backend only accepts UUID in candidateId filter. Keep only
        // version-level feedbacks (candidateId = null) on the client side.
        setOpenFeedbacks(
          (response.feedback ?? []).filter((item) => item.candidateId == null),
        );
      })
      .catch(() => {
        if (mounted) setOpenFeedbacks([]);
      });
    return () => {
      mounted = false;
    };
  }, [open, sopId, versionId]);

  useEffect(() => {
    if (!open) return;
    if (didAutoSelectFeedbacks.current) return;
    if (openFeedbacks.length === 0) return;
    const feedbackIds = openFeedbacks.map((feedback) => feedback.id);
    const feedbackStepIndices = Array.from(
      new Set(openFeedbacks.map((feedback) => feedback.stepIndex)),
    );
    const mergedStepIndices = Array.from(
      new Set([...selectedStepIndices, ...feedbackStepIndices]),
    );
    onSelectedFeedbackIdsChange(feedbackIds);
    onSelectedStepIndicesChange(mergedStepIndices);
    didAutoSelectFeedbacks.current = true;
  }, [
    open,
    openFeedbacks,
    onSelectedFeedbackIdsChange,
    onSelectedStepIndicesChange,
    selectedStepIndices,
  ]);

  const allFeedbackSelected =
    openFeedbacks.length > 0 &&
    openFeedbacks.every((feedback) => selectedFeedbackIds.includes(feedback.id));

  const handleToggleAllFeedbacks = () => {
    if (allFeedbackSelected) {
      onSelectedFeedbackIdsChange([]);
      return;
    }
    const allIds = openFeedbacks.map((feedback) => feedback.id);
    const allSteps = Array.from(
      new Set([
        ...selectedStepIndices,
        ...openFeedbacks.map((feedback) => feedback.stepIndex),
      ]),
    );
    onSelectedFeedbackIdsChange(allIds);
    onSelectedStepIndicesChange(allSteps);
  };

  const canContinue =
    (referenceFiles.length > 0 || selectedFeedbackIds.length > 0) &&
    !isRegenerating;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Update SOP with AI"
      size="medium"
    >
      <div id="regenerate-steps-modal-body" className="space-y-5 pr-2">
        <p className="text-sm text-slate-200/90">
          Upload files, select steps, and include open feedbacks to prepare the update.
        </p>

        <AccordionSection
          title="Reference files"
          open={sourcesOpen}
          onToggle={() => setSourcesOpen((v) => !v)}
        >
          <ReferenceFileUploader
            files={referenceFiles}
            onFilesChange={onReferenceFilesChange}
            defaultKind={defaultReferenceFileKind}
            onDefaultKindChange={onDefaultReferenceFileKindChange}
            referenceFileKinds={referenceFileKinds}
          />
        </AccordionSection>

        {openFeedbacks.length > 0 && (
          <div className="rounded-xl border border-slate-700/60 bg-slate-900/30">
            <div className="px-4 py-3 flex items-center justify-between">
              <button
                type="button"
                className="flex items-center justify-between flex-1 text-left text-sm font-medium text-slate-200 hover:text-slate-100 transition-colors"
                onClick={() => setFeedbackOpen((v) => !v)}
              >
                <span>Include open feedbacks</span>
                <span className="text-xs text-slate-400 ml-4">
                  {feedbackOpen ? "Hide" : "Show"}
                </span>
              </button>

            </div>
            {feedbackOpen && (
              <div className="px-4 pb-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-200">
                      Select feedback to include
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleAllFeedbacks}
                    className="text-xs text-blue-400 hover:text-blue-300 underline-offset-2 hover:underline"
                  >
                    {allFeedbackSelected ? "Deselect all" : "Select all"}
                  </button>
                </div>

                <div className="modal-body-scroll max-h-64 overflow-y-auto rounded-xl border border-slate-700/60 bg-slate-900/40 divide-y divide-slate-800/60 mr-2">
                  {openFeedbacks.map((feedback) => {
                    const checked = selectedFeedbackIds.includes(feedback.id);
                    return (
                      <label
                        key={feedback.id}
                        className={cn(
                          "flex items-start gap-3 px-4 py-3 cursor-pointer text-sm",
                          "hover:bg-slate-800/60 transition-colors",
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            if (checked) {
                              onSelectedFeedbackIdsChange(
                                selectedFeedbackIds.filter(
                                  (id) => id !== feedback.id,
                                ),
                              );
                            } else {
                              onSelectedFeedbackIdsChange([
                                ...selectedFeedbackIds,
                                feedback.id,
                              ]);
                              if (
                                !selectedStepIndices.includes(feedback.stepIndex)
                              ) {
                                onSelectedStepIndicesChange([
                                  ...selectedStepIndices,
                                  feedback.stepIndex,
                                ]);
                              }
                            }
                          }}
                          className="mt-0.5 h-4 w-4 rounded border-slate-600 text-blue-500 bg-slate-950 focus:ring-blue-500/40"
                        />
                        <div className="min-w-0 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center text-[10px] font-semibold rounded-full px-2 py-0.5 bg-slate-800/80 text-slate-200 border border-slate-600/60">
                              Step {feedback.stepIndex + 1}
                            </span>
                            <span className="truncate text-slate-100">
                              {feedback.category ?? "Feedback"}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-slate-400 line-clamp-2">
                            {feedback.comment}
                          </p>
                          {feedback.selectedText && (
                            <p className="mt-1 text-xs italic text-slate-500 line-clamp-1">
                              "{feedback.selectedText}"
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <AccordionSection
          title="Select steps"
          open={stepsOpen}
          onToggle={() => setStepsOpen((v) => !v)}
        >
          <StepSelectorList
            steps={steps}
            selectedIndices={selectedStepIndices}
            onSelectionChange={onSelectedStepIndicesChange}
          />
        </AccordionSection>

        {regenerateError && (
          <p className="text-xs text-red-300">{regenerateError}</p>
        )}

        <div
          className="sticky bottom-0 pt-4 mt-4 border-t border-slate-700/50 flex items-center justify-end gap-2"
          style={{ background: "#0f172a" }}
        >
          <Button
            type="button"
            disabled={isRegenerating}
            className="px-3 py-2 rounded-md border border-slate-600/60 bg-slate-900/40 text-xs font-semibold text-slate-200 hover:bg-slate-900/60 disabled:opacity-50"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canContinue}
            className="px-3 py-2 rounded-md border border-emerald-500/60 bg-emerald-500/15 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => void onPrimary()}
          >
            {isRegenerating ? "AI is preparing the update…" : primaryLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default StartReviewModal;
